
import { useEffect, useRef } from 'react';

export default function Home() {
  const heroRef = useRef<HTMLDivElement>(null);
  const servicesRef = useRef<HTMLDivElement>(null);
  const strategiesRef = useRef<HTMLDivElement>(null);
  const analyticsRef = useRef<HTMLDivElement>(null);
  const trendsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.5;
      const rate2 = scrolled * 0.3;
      const rate3 = scrolled * -0.2;

      if (heroRef.current) {
        heroRef.current.style.transform = `translateY(${rate}px)`;
      }
      if (servicesRef.current) {
        servicesRef.current.style.transform = `translateY(${rate2}px)`;
      }
      if (strategiesRef.current) {
        strategiesRef.current.style.transform = `translateY(${rate3}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    {
      title: "Search Engine Optimization",
      description: "Boost your website's visibility with advanced SEO strategies, keyword optimization, and content marketing that drives organic traffic and improves search rankings.",
      icon: "ri-search-line",
      gradient: "from-blue-600 to-purple-700"
    },
    {
      title: "Social Media Marketing",
      description: "Engage your audience across all social platforms with compelling content, targeted campaigns, and community management that builds brand loyalty.",
      icon: "ri-share-line",
      gradient: "from-pink-600 to-red-700"
    },
    {
      title: "Pay-Per-Click Advertising",
      description: "Maximize ROI with precision-targeted PPC campaigns across Google Ads, Facebook, and other platforms with real-time optimization.",
      icon: "ri-advertisement-line",
      gradient: "from-green-600 to-teal-700"
    },
    {
      title: "Content Marketing",
      description: "Create compelling narratives that resonate with your target audience through strategic content planning, creation, and distribution.",
      icon: "ri-article-line",
      gradient: "from-orange-600 to-yellow-700"
    },
    {
      title: "Email Marketing",
      description: "Build lasting relationships with personalized email campaigns, automation workflows, and segmentation strategies that convert.",
      icon: "ri-mail-line",
      gradient: "from-indigo-600 to-blue-700"
    },
    {
      title: "Marketing Analytics",
      description: "Make data-driven decisions with comprehensive analytics, performance tracking, and insights that optimize your marketing strategies.",
      icon: "ri-bar-chart-line",
      gradient: "from-purple-600 to-pink-700"
    }
  ];

  const strategies = [
    {
      title: "Brand Development",
      description: "Craft unique brand identities that stand out in competitive markets through strategic positioning, visual design, and brand storytelling.",
      metric: "300% Brand Recognition Increase",
      image: "https://readdy.ai/api/search-image?query=modern%20brand%20development%20workspace%20with%20creative%20team%20brainstorming%20brand%20identity%20concepts%2C%20sleek%20office%20environment%20with%20design%20boards%20and%20digital%20displays%2C%20professional%20lighting%2C%20contemporary%20business%20aesthetic&width=600&height=400&seq=brand1&orientation=landscape"
    },
    {
      title: "Customer Journey Mapping",
      description: "Design seamless customer experiences by mapping every touchpoint from awareness to conversion and beyond, optimizing each interaction.",
      metric: "85% Customer Satisfaction Rate",
      image: "https://readdy.ai/api/search-image?query=customer%20journey%20visualization%20with%20digital%20touchpoints%2C%20interactive%20customer%20experience%20map%2C%20modern%20office%20setting%20with%20data%20visualization%20screens%2C%20clean%20professional%20environment&width=600&height=400&seq=journey1&orientation=landscape"
    },
    {
      title: "Conversion Optimization",
      description: "Increase conversion rates through A/B testing, user experience optimization, and data-driven improvements to your digital assets.",
      metric: "250% Conversion Rate Improvement",
      image: "https://readdy.ai/api/search-image?query=conversion%20rate%20optimization%20dashboard%20with%20analytics%20graphs%2C%20modern%20digital%20marketing%20workspace%2C%20performance%20metrics%20displays%2C%20clean%20tech%20office%20environment&width=600&height=400&seq=conversion1&orientation=landscape"
    }
  ];

  const trends = [
    {
      title: "Artificial Intelligence in Marketing",
      description: "Leverage AI-powered tools for predictive analytics, personalization, and automated campaign optimization to stay ahead of the competition.",
      progress: 95,
      color: "blue"
    },
    {
      title: "Voice Search Optimization",
      description: "Optimize your content for voice search queries as smart speakers and voice assistants continue to reshape how people search for information.",
      progress: 78,
      color: "green"
    },
    {
      title: "Interactive Content Marketing",
      description: "Engage audiences with interactive content like polls, quizzes, AR experiences, and virtual events that drive higher engagement rates.",
      progress: 88,
      color: "purple"
    },
    {
      title: "Privacy-First Marketing",
      description: "Adapt to cookie-less marketing strategies and privacy regulations while maintaining effective targeting and personalization.",
      progress: 72,
      color: "orange"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          ref={heroRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=futuristic%20digital%20marketing%20landscape%20with%20holographic%20displays%2C%20neon%20data%20streams%2C%20advanced%20technology%20workspace%2C%20cyberpunk%20aesthetic%20with%20dark%20blue%20and%20purple%20gradients%2C%20high-tech%20marketing%20environment&width=1920&height=4000&seq=hero1&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900/80 z-10" />
        
        <div className="relative z-20 text-center max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="transform hover:scale-105 transition-all duration-700 ease-out">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent animate-pulse">
              Digital Marketing Evolution
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl mb-8 text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Transform your business with cutting-edge digital marketing strategies that drive results in the modern landscape
            </p>
            <button className="group bg-gradient-to-r from-blue-600 to-purple-700 hover:from-blue-700 hover:to-purple-800 px-8 sm:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-xl font-semibold transition-all duration-500 transform hover:scale-110 hover:shadow-2xl whitespace-nowrap cursor-pointer">
              <span className="relative z-10">Explore Services</span>
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </button>
          </div>
        </div>

        {/* Floating 3D Elements */}
        <div className="absolute top-1/4 left-1/4 w-16 h-16 sm:w-20 sm:h-20 bg-blue-500/30 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
        <div className="absolute top-1/3 right-1/4 w-12 h-12 sm:w-16 sm:h-16 bg-purple-500/30 rounded-full animate-bounce" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-1/4 left-1/3 w-14 h-14 sm:w-18 sm:h-18 bg-pink-500/30 rounded-full animate-bounce" style={{ animationDelay: '2s' }} />
      </section>

      {/* Services Section */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div 
          ref={servicesRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=digital%20marketing%20services%20visualization%20with%20interconnected%20network%20nodes%2C%20glowing%20connections%2C%20modern%20tech%20background%20with%20gradient%20colors%2C%20abstract%20digital%20landscape%2C%20professional%20business%20environment&width=1920&height=4000&seq=services1&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              Our Digital Marketing Services
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Comprehensive solutions to elevate your digital presence and drive measurable results
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-lg rounded-2xl p-6 sm:p-8 hover:scale-105 transition-all duration-700 cursor-pointer border border-gray-700/50 hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/25"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${service.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:rotate-12 transition-transform duration-500`}>
                  <i className={`${service.icon} text-2xl sm:text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-4 text-white group-hover:text-blue-400 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300 leading-relaxed">
                  {service.description}
                </p>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-700/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strategies Section */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div 
          ref={strategiesRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=strategic%20marketing%20planning%20environment%20with%20data%20analytics%20visualizations%2C%20modern%20boardroom%20with%20digital%20displays%2C%20professional%20strategy%20session%2C%20clean%20corporate%20aesthetic%20with%20blue%20and%20purple%20lighting&width=1920&height=4000&seq=strategy1&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/75 to-gray-900/85 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Strategic Marketing Approaches
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Data-driven strategies that transform your marketing performance and accelerate growth
            </p>
          </div>

          <div className="space-y-12 sm:space-y-20">
            {strategies.map((strategy, index) => (
              <div 
                key={index}
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 sm:gap-12 lg:gap-16`}
              >
                <div className="flex-1 group">
                  <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 hover:scale-105 transition-all duration-700 border border-gray-700/50 hover:border-purple-500/50 hover:shadow-2xl hover:shadow-purple-500/25">
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 text-white group-hover:text-purple-400 transition-colors duration-300">
                      {strategy.title}
                    </h3>
                    <p className="text-gray-300 text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                      {strategy.description}
                    </p>
                    <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full inline-block font-semibold text-sm sm:text-base">
                      {strategy.metric}
                    </div>
                  </div>
                </div>
                
                <div className="flex-1 group">
                  <div className="relative overflow-hidden rounded-3xl hover:scale-105 transition-all duration-700 cursor-pointer">
                    <img 
                      src={strategy.image} 
                      alt={strategy.title}
                      className="w-full h-64 sm:h-80 lg:h-96 object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div 
          ref={analyticsRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=advanced%20marketing%20analytics%20dashboard%20with%20real-time%20data%20visualization%2C%20multiple%20screens%20showing%20performance%20metrics%2C%20modern%20data%20center%20environment%2C%20holographic%20charts%20and%20graphs%2C%20futuristic%20analytics%20workspace&width=1920&height=4000&seq=analytics1&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-900/80 to-gray-900/90 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
              Advanced Marketing Analytics
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Real-time insights and performance tracking to optimize your marketing investments
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-12 sm:mb-16">
            <div className="group bg-gradient-to-br from-blue-900/50 to-blue-800/50 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center hover:scale-105 transition-all duration-500 cursor-pointer border border-blue-700/50 hover:border-blue-400/70 hover:shadow-2xl hover:shadow-blue-500/25">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-400 mb-2 group-hover:scale-110 transition-transform duration-300">500M+</div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-300">Data Points Analyzed</div>
            </div>
            
            <div className="group bg-gradient-to-br from-green-900/50 to-green-800/50 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center hover:scale-105 transition-all duration-500 cursor-pointer border border-green-700/50 hover:border-green-400/70 hover:shadow-2xl hover:shadow-green-500/25">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-green-400 mb-2 group-hover:scale-110 transition-transform duration-300">98.7%</div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-300">Accuracy Rate</div>
            </div>
            
            <div className="group bg-gradient-to-br from-purple-900/50 to-purple-800/50 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center hover:scale-105 transition-all duration-500 cursor-pointer border border-purple-700/50 hover:border-purple-400/70 hover:shadow-2xl hover:shadow-purple-500/25">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-purple-400 mb-2 group-hover:scale-110 transition-transform duration-300">24/7</div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-300">Real-time Monitoring</div>
            </div>
            
            <div className="group bg-gradient-to-br from-orange-900/50 to-orange-800/50 backdrop-blur-lg rounded-2xl p-6 sm:p-8 text-center hover:scale-105 transition-all duration-500 cursor-pointer border border-orange-700/50 hover:border-orange-400/70 hover:shadow-2xl hover:shadow-orange-500/25">
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-orange-400 mb-2 group-hover:scale-110 transition-transform duration-300">350%</div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-300">ROI Improvement</div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-12 hover:scale-105 transition-all duration-700 border border-gray-700/50 hover:border-orange-500/50 hover:shadow-2xl hover:shadow-orange-500/25">
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 text-center text-white">
              Performance Dashboard
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Website Traffic</span>
                  <span className="text-blue-400 font-semibold text-sm sm:text-base">+285%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 sm:h-3 rounded-full w-[85%] animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Conversion Rate</span>
                  <span className="text-green-400 font-semibold text-sm sm:text-base">+167%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 sm:h-3 rounded-full w-[92%] animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Lead Generation</span>
                  <span className="text-purple-400 font-semibold text-sm sm:text-base">+320%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 sm:h-3 rounded-full w-[78%] animate-pulse"></div>
                </div>
              </div>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Social Engagement</span>
                  <span className="text-pink-400 font-semibold text-sm sm:text-base">+195%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-pink-500 to-pink-600 h-2 sm:h-3 rounded-full w-[88%] animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Email Open Rate</span>
                  <span className="text-yellow-400 font-semibold text-sm sm:text-base">+142%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-2 sm:h-3 rounded-full w-[75%] animate-pulse"></div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm sm:text-base">Brand Awareness</span>
                  <span className="text-orange-400 font-semibold text-sm sm:text-base">+275%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3">
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 sm:h-3 rounded-full w-[95%] animate-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trends Section */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div 
          ref={trendsRef}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=future%20of%20digital%20marketing%20trends%20visualization%2C%20emerging%20technology%20concepts%2C%20AI%20and%20machine%20learning%20in%20marketing%2C%20innovative%20digital%20transformation%2C%20modern%20tech%20landscape%20with%20holographic%20elements&width=1920&height=4000&seq=trends1&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/85 via-gray-900/75 to-gray-900/95 z-10" />
        
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Future Marketing Trends
            </h2>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Stay ahead with emerging trends and technologies shaping the future of digital marketing
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {trends.map((trend, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-gray-800/60 to-gray-900/60 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 hover:scale-105 transition-all duration-700 cursor-pointer border border-gray-700/50 hover:border-cyan-500/50 hover:shadow-2xl hover:shadow-cyan-500/25"
              >
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 text-white group-hover:text-cyan-400 transition-colors duration-300">
                  {trend.title}
                </h3>
                <p className="text-gray-300 text-sm sm:text-base lg:text-lg mb-6 sm:mb-8 leading-relaxed group-hover:text-gray-200 transition-colors duration-300">
                  {trend.description}
                </p>
                
                <div className="flex items-center justify-between mb-2 sm:mb-3">
                  <span className="text-gray-400 text-sm sm:text-base">Adoption Rate</span>
                  <span className={`font-bold text-sm sm:text-base text-${trend.color}-400`}>{trend.progress}%</span>
                </div>
                
                <div className="w-full bg-gray-700 rounded-full h-2 sm:h-3 overflow-hidden">
                  <div 
                    className={`bg-gradient-to-r from-${trend.color}-500 to-${trend.color}-600 h-2 sm:h-3 rounded-full transition-all duration-1000 ease-out`}
                    style={{ width: `${trend.progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 sm:mt-20 text-center">
            <button className="group bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-700 hover:to-blue-800 px-8 sm:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-xl font-semibold transition-all duration-500 transform hover:scale-110 hover:shadow-2xl whitespace-nowrap cursor-pointer">
              <span className="relative z-10">Get Started Today</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
